import React, { useEffect, useState } from "react";
import { TEAMS } from "../../data/tournament";
import { api, formatApiError } from "../../lib/api";
import {
  Save,
  Image as ImageIcon,
  Plus,
  Trash2,
  Edit3,
  Shield,
  X,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  Download,
  ListPlus,
} from "lucide-react";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const POSITIONS = [
  { v: "G", label: "Gardien" },
  { v: "D", label: "Défenseur" },
  { v: "A", label: "Attaquant" },
];

const TeamsPanel = () => {
  const [serverTeams, setServerTeams] = useState({});
  const [openTeam, setOpenTeam] = useState(null);

  const refresh = () => {
    api.get("/teams").then((r) => {
      const m = {};
      r.data.forEach((t) => (m[t.team_id] = t));
      setServerTeams(m);
    }).catch(() => {});
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div data-testid="admin-teams-panel">
      <div className="mb-6">
        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
          Logos & Alignements
        </span>
        <h2 className="mt-1 font-display text-4xl uppercase">Gestion des équipes</h2>
        <p className="mt-2 text-white/55 text-sm">
          Téléversez le logo officiel de chaque équipe et ajoutez les joueurs (numéro, nom, position, photo).
        </p>
      </div>

      <div className="space-y-3">
        {TEAMS.map((t) => {
          const sv = serverTeams[t.id];
          const isOpen = openTeam === t.id;
          return (
            <div key={t.id} className="bg-[#111] border border-white/10" data-testid={`admin-team-${t.id}`}>
              <button
                onClick={() => setOpenTeam(isOpen ? null : t.id)}
                className="w-full px-4 py-3 flex items-center gap-4 hover:bg-white/5 transition"
                data-testid={`admin-team-toggle-${t.id}`}
              >
                <div className="w-12 h-12 bg-black border border-white/10 flex items-center justify-center overflow-hidden">
                  {sv?.logo_base64 ? (
                    <img src={sv.logo_base64} alt={t.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <Shield className="w-6 h-6 text-[#E60012]/60" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-display text-xl uppercase">{t.name}</div>
                  <div className="text-xs text-white/50 font-condensed uppercase tracking-wider">
                    Cat. {t.category} {sv?.logo_base64 ? "· Logo OK" : "· Pas de logo"}
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
              </button>

              {isOpen && <TeamEditor teamMeta={t} server={sv} onChange={refresh} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TeamEditor = ({ teamMeta, server, onChange }) => {
  const [logo, setLogo] = useState(server?.logo_base64 || null);
  const [motto, setMotto] = useState(server?.motto || "");
  const [description, setDescription] = useState(server?.description || "");
  const [savingMeta, setSavingMeta] = useState(false);
  const [roster, setRoster] = useState([]);
  const [editingPlayer, setEditingPlayer] = useState(null);

  const refreshRoster = () => {
    api.get(`/teams/${teamMeta.id}/roster`).then((r) => setRoster(r.data)).catch(() => setRoster([]));
  };

  useEffect(() => {
    refreshRoster();
    setLogo(server?.logo_base64 || null);
    setMotto(server?.motto || "");
    setDescription(server?.description || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMeta.id]);

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      alert("Image trop volumineuse (max 4 Mo)");
      return;
    }
    setLogo(await fileToBase64(f));
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await api.put(`/admin/teams/${teamMeta.id}`, {
        logo_base64: logo,
        motto,
        description,
      });
      onChange();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingMeta(false);
    }
  };

  const removePlayer = async (id) => {
    if (!confirm("Retirer ce joueur ?")) return;
    try {
      await api.delete(`/admin/teams/${teamMeta.id}/roster/${id}`);
      refreshRoster();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="border-t border-white/10 p-4 sm:p-6 space-y-6">
      {/* Logo + meta */}
      <div className="grid md:grid-cols-[140px_1fr] gap-5">
        <div>
          <div className="w-32 h-32 bg-black border border-white/10 flex items-center justify-center overflow-hidden mb-2">
            {logo ? (
              <img src={logo} alt="logo" className="w-full h-full object-contain p-2" />
            ) : (
              <Shield className="w-12 h-12 text-[#E60012]/60" />
            )}
          </div>
          <label className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#E60012] cursor-pointer font-condensed uppercase tracking-[0.15em] text-xs">
            <ImageIcon className="w-3.5 h-3.5" /> Logo
            <input type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid={`logo-input-${teamMeta.id}`} />
          </label>
          {logo && (
            <button onClick={() => setLogo(null)} className="ml-2 text-xs text-white/55 hover:text-red-400 underline">
              Retirer
            </button>
          )}
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60 block mb-1">Devise (optionnel)</span>
            <input
              type="text"
              value={motto}
              onChange={(e) => setMotto(e.target.value)}
              data-testid={`motto-${teamMeta.id}`}
              className="w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
              placeholder='ex. "Fort, fier, ensemble"'
            />
          </label>
          <label className="block">
            <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60 block mb-1">Description (optionnel)</span>
            <textarea
              value={description}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              data-testid={`desc-${teamMeta.id}`}
              className="w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012] resize-y"
              placeholder="Présentez l'équipe en quelques lignes..."
            />
          </label>
          <div>
            <button
              onClick={saveMeta}
              disabled={savingMeta}
              data-testid={`save-team-${teamMeta.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 text-white font-condensed uppercase tracking-[0.15em] text-xs"
            >
              <Save className="w-3.5 h-3.5" /> {savingMeta ? "Enregistrement…" : "Enregistrer logo + infos"}
            </button>
          </div>
        </div>
      </div>

      {/* Roster */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-display text-xl uppercase">Alignement ({roster.length})</h4>
          <div className="flex items-center gap-2">
            <BulkAddRoster teamId={teamMeta.id} onDone={refreshRoster} />
            <CsvImport teamId={teamMeta.id} onDone={refreshRoster} />
            <button
              onClick={() => setEditingPlayer({ number: "", name: "", position: "A", photo_base64: null })}
              data-testid={`add-player-${teamMeta.id}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.15em] text-xs"
            >
              <Plus className="w-3.5 h-3.5" /> Ajouter joueur
            </button>
          </div>
        </div>

        {roster.length === 0 ? (
          <div className="text-white/50 text-sm font-condensed uppercase tracking-wider py-6 text-center bg-black/30">
            Aucun joueur encore.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {roster.map((p) => (
              <div key={p.id} className="bg-black border border-white/10 p-3 flex gap-3" data-testid={`admin-player-${p.id}`}>
                <div className="w-14 h-14 bg-black border border-white/10 overflow-hidden shrink-0">
                  {p.photo_base64 ? (
                    <img src={p.photo_base64} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-[#E60012]/40" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#E60012] font-display text-lg leading-none">#{p.number || "—"}</div>
                  <div className="font-condensed text-sm truncate">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 font-condensed">{p.position || "—"}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setEditingPlayer(p)}
                    data-testid={`edit-player-${p.id}`}
                    className="text-white/60 hover:text-[#E60012]"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => removePlayer(p.id)}
                    data-testid={`del-player-${p.id}`}
                    className="text-white/60 hover:text-red-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingPlayer && (
        <PlayerEditor
          team={teamMeta}
          initial={editingPlayer}
          onClose={() => setEditingPlayer(null)}
          onSaved={() => {
            setEditingPlayer(null);
            refreshRoster();
          }}
        />
      )}
    </div>
  );
};

const PlayerEditor = ({ team, initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      setError("Photo trop volumineuse (max 3 Mo).");
      return;
    }
    setF("photo_base64", await fileToBase64(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        number: form.number || "",
        name: form.name,
        position: form.position || "",
        photo_base64: form.photo_base64 || null,
      };
      if (form.id) {
        await api.put(`/admin/teams/${team.id}/roster/${form.id}`, payload);
      } else {
        await api.post(`/admin/teams/${team.id}/roster`, payload);
      }
      onSaved();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start justify-center" data-testid="player-editor-modal">
      <form onSubmit={submit} className="relative w-full max-w-md my-10 bg-[#0f0f0f] border border-white/10 p-6">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-black/70 hover:bg-[#E60012] flex items-center justify-center" data-testid="player-editor-close">
          <X className="w-4 h-4" />
        </button>
        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">{team.name}</span>
        <h2 className="mt-1 font-display text-2xl uppercase">{form.id ? "Modifier le joueur" : "Ajouter un joueur"}</h2>

        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-20 h-20 bg-black border border-white/10 overflow-hidden">
              {form.photo_base64 ? (
                <img src={form.photo_base64} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-[#E60012]/40" />
                </div>
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#E60012] cursor-pointer font-condensed uppercase tracking-[0.15em] text-xs">
              <ImageIcon className="w-3.5 h-3.5" /> Photo
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="player-photo-input" />
            </label>
            {form.photo_base64 && (
              <button type="button" onClick={() => setF("photo_base64", null)} className="text-xs text-white/55 hover:text-red-400">
                Retirer
              </button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Numéro</span>
              <input
                type="text"
                value={form.number || ""}
                onChange={(e) => setF("number", e.target.value)}
                data-testid="player-number-input"
                className="mt-1 w-full bg-black border border-white/10 px-2 py-2 outline-none focus:border-[#E60012] font-display text-xl text-center"
                placeholder="9"
              />
            </label>
            <label className="block col-span-2">
              <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Nom</span>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setF("name", e.target.value)}
                data-testid="player-name-input"
                className="mt-1 w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
                placeholder="Nom du joueur"
              />
            </label>
          </div>

          <div>
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Position</span>
            <div className="mt-1 flex gap-2">
              {POSITIONS.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setF("position", p.v)}
                  data-testid={`player-pos-${p.v}`}
                  className={`flex-1 px-3 py-2 font-condensed uppercase tracking-[0.15em] text-xs border ${
                    form.position === p.v
                      ? "bg-[#E60012] text-white border-[#E60012]"
                      : "border-white/15 text-white/70 hover:border-[#E60012]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="mt-3 text-sm text-red-400">{error}</div>}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="player-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 font-condensed uppercase tracking-[0.15em] text-xs"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "..." : form.id ? "Mettre à jour" : "Ajouter"}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/15 hover:border-white/40 font-condensed uppercase tracking-[0.15em] text-xs">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

const BulkAddRoster = ({ teamId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [position, setPosition] = useState("A");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const lines = parseBulkRoster(text);

  const submit = async () => {
    if (lines.length === 0) {
      setError("Aucun joueur valide à importer.");
      return;
    }
    setBusy(true);
    setError("");
    let added = 0;
    let errors = [];
    for (const ln of lines) {
      try {
        await api.post(`/admin/teams/${teamId}/roster`, {
          number: ln.number,
          name: ln.name,
          position: position.toUpperCase(),
          photo_base64: null,
        });
        added++;
      } catch (e) {
        errors.push(`${ln.number} ${ln.name}: ${formatApiError(e.response?.data?.detail) || e.message}`);
      }
    }
    setBusy(false);
    onDone();
    if (errors.length === 0) {
      setOpen(false);
      setText("");
      alert(`${added} joueur(s) ajouté(s).`);
    } else {
      setError(`${added} ajouté(s), ${errors.length} erreur(s) :\n${errors.slice(0, 3).join("\n")}`);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`bulk-add-${teamId}`}
        className="inline-flex items-center gap-1 px-2 py-1.5 border border-white/15 hover:border-[#E60012] text-white/80 font-condensed uppercase tracking-[0.15em] text-[10px]"
      >
        <ListPlus className="w-3 h-3" /> Ajout multiple
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div
            className="bg-[#111] border border-white/15 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="bulk-add-modal"
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display text-2xl uppercase">Ajout multiple</h3>
              <button type="button" onClick={() => !busy && setOpen(false)} className="p-1 hover:text-[#E60012]" disabled={busy}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="text-xs text-white/60 font-condensed leading-relaxed">
                Un joueur par ligne au format <span className="text-white">numéro nom complet</span>.
                Séparateur : espace, virgule, point-virgule ou tabulation. Exemples valides :
              </div>
              <div className="bg-black/40 border border-white/10 px-4 py-3 font-mono text-xs text-white/65 whitespace-pre">
{`9 Alex Tremblay
12, Marc Bouchard
#27 Léa Dupuis
1   Wayne Gretzky`}
              </div>

              <div>
                <div className="text-[10px] font-condensed uppercase tracking-[0.25em] text-white/55 mb-1.5">
                  Liste des joueurs
                </div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={10}
                  data-testid="bulk-add-textarea"
                  placeholder={"9 Alex Tremblay\n4 Marc Dupuis\n1 Léo Bélanger"}
                  className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#E60012]"
                />
                <div className="mt-1 text-[10px] text-white/50 font-condensed uppercase tracking-wider">
                  {lines.length} joueur{lines.length > 1 ? "s" : ""} détecté{lines.length > 1 ? "s" : ""}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-condensed uppercase tracking-[0.25em] text-white/55 mb-1.5">
                  Position assignée à tous
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["A", "D", "G"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPosition(p)}
                      data-testid={`bulk-add-pos-${p}`}
                      className={`px-3 py-2 border text-xs font-condensed uppercase tracking-wider ${
                        position === p
                          ? "bg-[#E60012]/15 border-[#E60012] text-white"
                          : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {p === "A" ? "Attaquant" : p === "D" ? "Défenseur" : "Gardien"}
                    </button>
                  ))}
                </div>
                <div className="mt-1 text-[10px] text-white/40 font-condensed">
                  Vous pourrez modifier la position individuellement après l'import.
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-condensed whitespace-pre-line">
                  {error}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-4 py-2 border border-white/15 hover:border-white/40 font-condensed uppercase tracking-[0.2em] text-xs"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy || lines.length === 0}
                data-testid="bulk-add-save"
                className="inline-flex items-center gap-2 px-5 py-2 bg-[#E60012] text-white font-condensed uppercase tracking-[0.2em] text-xs hover:brightness-110 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {busy ? "Import…" : `Ajouter ${lines.length || ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Parses one line per player. Accepts leading "#", separators: space/tab/comma/semicolon.
// First token = number (digits, possibly with #), rest = full name.
function parseBulkRoster(text) {
  const out = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^#?\s*(\d+)\s*[,;\t\s]\s*(.+)$/);
    if (!m) continue;
    const number = m[1];
    const name = m[2].trim().replace(/[,;]+$/, "").trim();
    if (!name) continue;
    out.push({ number, name });
  }
  return out;
}

const CsvImport = ({ teamId, onDone }) => {
  const [busy, setBusy] = useState(false);

  const downloadTemplate = () => {
    const csv = "number,name,position\n9,Alex Tremblay,A\n4,Marc Dupuis,D\n1,Léo Bélanger,G\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `roster_${teamId}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.csv$/i.test(f.name) && f.type !== "text/csv") {
      alert("Veuillez choisir un fichier .csv");
      return;
    }
    const text = await f.text();
    const rows = parseCsv(text);
    if (rows.length === 0) {
      alert("Aucune ligne valide trouvée.");
      return;
    }
    if (!confirm(`Importer ${rows.length} joueur(s) ?`)) return;
    setBusy(true);
    let added = 0;
    let errors = 0;
    for (const r of rows) {
      try {
        await api.post(`/admin/teams/${teamId}/roster`, {
          number: r.number || "",
          name: r.name,
          position: (r.position || "").toUpperCase(),
          photo_base64: null,
        });
        added++;
      } catch {
        errors++;
      }
    }
    setBusy(false);
    alert(`${added} joueur(s) ajouté(s)${errors ? ` · ${errors} erreur(s)` : ""}.`);
    onDone();
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={downloadTemplate}
        title="Télécharger un modèle CSV"
        data-testid={`csv-template-${teamId}`}
        className="inline-flex items-center gap-1 px-2 py-1.5 border border-white/15 hover:border-[#E60012] text-white/70 font-condensed uppercase tracking-[0.15em] text-[10px]"
      >
        <Download className="w-3 h-3" />
        Modèle
      </button>
      <label
        className={`inline-flex items-center gap-1 px-2 py-1.5 border border-white/15 hover:border-[#E60012] text-white/80 font-condensed uppercase tracking-[0.15em] text-[10px] cursor-pointer ${
          busy ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <FileSpreadsheet className="w-3 h-3" /> {busy ? "Import…" : "Importer CSV"}
        <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid={`csv-import-${teamId}`} />
      </label>
    </div>
  );
};

// Minimal CSV parser supporting quoted fields, comma-separated, optional header
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const splitLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (c === '"') {
          inQ = false;
        } else {
          cur += c;
        }
      } else {
        if (c === '"') inQ = true;
        else if (c === ",") {
          out.push(cur);
          cur = "";
        } else {
          cur += c;
        }
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  let header = null;
  const first = splitLine(lines[0]).map((h) => h.toLowerCase());
  const headerKeywords = ["number", "name", "position", "numero", "numéro", "nom", "poste"];
  if (first.some((c) => headerKeywords.includes(c))) {
    header = first;
    lines.shift();
  }

  const idxOf = (...names) => {
    if (!header) return -1;
    for (const n of names) {
      const i = header.indexOf(n);
      if (i !== -1) return i;
    }
    return -1;
  };

  const numIdx = header ? idxOf("number", "numero", "numéro", "no", "#") : 0;
  const nameIdx = header ? idxOf("name", "nom") : 1;
  const posIdx = header ? idxOf("position", "poste") : 2;

  const out = [];
  for (const line of lines) {
    const parts = splitLine(line);
    const name = (parts[nameIdx] || "").trim();
    if (!name) continue;
    out.push({
      number: (parts[numIdx] || "").trim(),
      name,
      position: (parts[posIdx] || "").trim().toUpperCase().slice(0, 1),
    });
  }
  return out;
}

export default TeamsPanel;
